import { Box, Grid, IconButton, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useMemo } from 'react';
import FormsPopup from '../../common/FormsPopup/FormsPopup';
import UserAvatar from '../../common/UserAvatar/UserAvatar';
import { EditMemberProps } from './types';
import styles from './styles';
import InputField from '../../common/InputField/InputField';
import CustomSelect from '../../common/Select';
import { getUserStatus } from '../../../containers/PartnerAndMembers';
import { getPartners } from '../../../containers/PartnerAndMembers/api';
import AsyncAutoComplete from '../../common/AsyncAutoSuggest';
import USState from '../../../utils/USState';
import FormButton from '../../common/FormButton';

const genderData = [
    { name: 'Man', value: 1 },
    { name: 'Woman', value: 2 },
    { name: 'Transgender', value: 3 },
    { name: 'Non-binary/non-conforming', value: 4 },
    { name: 'Prefer not to respond', value: 5 },
];

const DiabetesTypeData = [
    { name: 'Type 1', value: 1 },
    { name: 'Type 2', value: 2 },
    { name: 'LADA', value: 3 },
    { name: 'Gestational', value: 4 },
    { name: 'Other', value: 5 },
];

const EditMember = ({
    editMemberDetails,
    handleCloseEditMember,
    memberInfo,
    formErrors,
    handleChangeAvatar,
    handleInput,
    handleSubmit,
    selectedImage,
    handleCheckItem,
    fromSelectedPartner,
    setChangeStatus,
    setMemberInfo,
    memberAddressess,
    handleAddMemberPrimaryAddress,
    handleUpdateAdditionalAddress,
    submitting,
}: EditMemberProps) => {
    const primaryAddress = useMemo(() => {
        return memberAddressess?.find((a) => a.isDefault);
    }, [memberAddressess]);

    return (
        <FormsPopup
            id="edit_member"
            open={editMemberDetails}
            title="Member Details"
            textAlign="left"
            handleClose={handleCloseEditMember}
            width={662}
        >
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <Typography component="div" sx={{ marginBottom: '24px' }}>
                    <UserAvatar
                        name={`${memberInfo?.firstname} ${memberInfo?.lastname}`}
                        source={
                            selectedImage ||
                            (memberInfo?.signedImageUrl &&
                            memberInfo?.signedImageUrl !== 'null' &&
                            memberInfo?.signedImageUrl !== 'undefined'
                                ? memberInfo?.signedImageUrl
                                : '')
                        }
                        size={80}
                        radius="8px"
                        fontSize={20}
                        isEditing
                        handleChangeAvatar={handleChangeAvatar}
                    />
                </Typography>
                <Box>
                    <Typography sx={styles.member_label}>Status</Typography>
                    <Box sx={styles.statusContainer}>
                        <IconButton data-testid="userStatus" onClick={() => setChangeStatus(true)} sx={{ padding: 0 }}>
                            <img
                                alt="status"
                                src={
                                    memberInfo?.userStatusId === '1' || memberInfo?.userStatusId === '2'
                                        ? '/images/active-toggle.png'
                                        : '/images/disable-toggle.png'
                                }
                            />
                        </IconButton>
                        <Typography sx={styles.statusText}>
                            {getUserStatus(String(memberInfo?.userStatusId))}
                        </Typography>
                    </Box>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <InputField
                            required
                            id="firstname"
                            label="First Name"
                            type="text"
                            name="firstname"
                            placeholder="Type here..."
                            onChange={handleInput}
                            value={memberInfo?.firstname}
                            error={formErrors && formErrors.firstname && formErrors.firstname[0] !== ''}
                            errorMessage={formErrors && formErrors.firstname && formErrors.firstname[0]}
                        />

                        <Typography sx={styles.profileField} component="div">
                            <InputField
                                required
                                id="email"
                                label="Email"
                                type="email"
                                name="email"
                                placeholder="Type here..."
                                onChange={handleInput}
                                value={memberInfo?.email}
                                disabled
                                error={formErrors && formErrors.email && formErrors.email[0] !== ''}
                                errorMessage={formErrors && formErrors.email && formErrors.email[0]}
                            />
                        </Typography>

                        <Typography sx={styles.profileField} component="div">
                            <CustomSelect
                                name="patientCondition"
                                label="Diabetes Type"
                                id="patientCondition"
                                handleOnchange={handleInput}
                                items={DiabetesTypeData}
                                value={memberInfo?.patientCondition}
                                error={
                                    formErrors && formErrors.patientCondition && formErrors.patientCondition[0] !== ''
                                }
                                errorMessage={
                                    formErrors && formErrors.patientCondition && formErrors.patientCondition[0]
                                }
                                handleCheckItem={handleCheckItem}
                                multiple
                            />
                        </Typography>

                        {memberInfo?.patientCondition.includes(5) ? (
                            <Typography sx={styles.profileField} component="div">
                                <InputField
                                    required
                                    id="otherType"
                                    label="Other Type"
                                    type="text"
                                    name="otherType"
                                    placeholder="Type here..."
                                    onChange={handleInput}
                                    value={memberInfo?.otherType}
                                />
                            </Typography>
                        ) : null}

                        <Typography sx={styles.profileField} component="div">
                            <InputField
                                required={false}
                                id="subscriberId"
                                label="Subsciber ID"
                                type="text"
                                name="subscriberId"
                                placeholder="Type here..."
                                onChange={handleInput}
                                value={memberInfo?.subscriberId}
                                error={formErrors && formErrors.subscriberId && formErrors.subscriberId[0] !== ''}
                                errorMessage={formErrors && formErrors.subscriberId && formErrors.subscriberId[0]}
                            />
                        </Typography>
                        <Typography sx={styles.profileField} component="div">
                            <AsyncAutoComplete
                                id="employerId"
                                async={async (value) => {
                                    const res = await getPartners({
                                        currentPage: 1,
                                        partnerSearchText: value,
                                        partnerSize: 10000,
                                        selectedPartnerPeriod: '',
                                        selectedPartnerStatus: 'active',
                                        sort: 'name',
                                        order: 'asc',
                                    });
                                    return res.data.data.rows;
                                }}
                                label="Partner"
                                optionLabel="name"
                                defaultValue={{
                                    name: memberInfo?.employer?.name || '',
                                    id: memberInfo?.employerId || '',
                                }}
                                handleSelectedoption={(selectedOption) => {
                                    if (memberInfo && setMemberInfo) {
                                        setMemberInfo({
                                            ...memberInfo,
                                            employerId:
                                                selectedOption && selectedOption.id ? selectedOption?.id : undefined,
                                        });
                                    }
                                }}
                                disabled={fromSelectedPartner}
                                error={formErrors && formErrors.employerId && formErrors.employerId[0] !== ''}
                                errorMessage={formErrors && formErrors.employerId && formErrors.employerId[0]}
                            />
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <InputField
                            required
                            id="lastname"
                            label="Last Name"
                            type="text"
                            name="lastname"
                            placeholder="Type here..."
                            onChange={handleInput}
                            value={memberInfo?.lastname}
                            error={formErrors && formErrors.lastname && formErrors.lastname[0] !== ''}
                            errorMessage={formErrors && formErrors.lastname && formErrors.lastname[0]}
                        />

                        <Typography sx={styles.profileField} component="div">
                            <InputField
                                required
                                id="phone"
                                label="Phone Number"
                                type="text"
                                name="phone"
                                placeholder="Type here..."
                                onChange={handleInput}
                                value={memberInfo?.phone}
                                error={formErrors && formErrors.phone && formErrors.phone[0] !== ''}
                                errorMessage={formErrors && formErrors.phone && formErrors.phone[0]}
                            />
                        </Typography>

                        <Typography sx={styles.profileField} component="div">
                            <CustomSelect
                                name="gender"
                                label="Gender"
                                id="gender"
                                handleOnchange={handleInput}
                                items={genderData}
                                value={memberInfo?.gender}
                                error={formErrors && formErrors.gender && formErrors.gender[0] !== ''}
                                errorMessage={formErrors && formErrors.gender && formErrors.gender[0]}
                            />
                        </Typography>

                        <Typography sx={styles.profileField} component="div">
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    inputFormat="MM/DD/YYYY"
                                    value={
                                        memberInfo?.dateOfBirth
                                            ? moment(memberInfo.dateOfBirth).format('MM/DD/YYYY')
                                            : ''
                                    }
                                    onChange={(newValue) => {
                                        if (memberInfo && setMemberInfo) {
                                            setMemberInfo({
                                                ...memberInfo,
                                                dateOfBirth: moment(newValue).format('MM/DD/YYYY'),
                                            });
                                        }
                                    }}
                                    label="Date of Birth"
                                    maxDate={new Date()}
                                    renderInput={(params) => (
                                        <InputField
                                            {...params}
                                            required={false}
                                            id="dateOfBirth"
                                            name="dateOfBirth"
                                            value={
                                                memberInfo?.dateOfBirth
                                                    ? moment(memberInfo.dateOfBirth).format('MM/DD/YYYY')
                                                    : ''
                                            }
                                            error={
                                                formErrors && formErrors.dateOfBirth && formErrors.dateOfBirth[0] !== ''
                                            }
                                            errorMessage={
                                                formErrors && formErrors.dateOfBirth && formErrors.dateOfBirth[0]
                                            }
                                            placeholder="MM/DD/YYYY"
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Typography>
                    </Grid>
                </Grid>
                {primaryAddress ? (
                    <Box>
                        <Typography sx={styles.inputLabel}>Primary Address</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography sx={styles.profileField} component="div">
                                    <InputField
                                        required
                                        id="address1"
                                        label="Address"
                                        type="text"
                                        name="address1"
                                        placeholder="Type here..."
                                        onChange={handleInput}
                                        value={memberInfo?.address1}
                                        error={formErrors && formErrors.address1 && formErrors.address1[0] !== ''}
                                        errorMessage={formErrors && formErrors.address1 && formErrors.address1[0]}
                                    />
                                </Typography>
                                <Typography sx={styles.profileField} component="div">
                                    <InputField
                                        required
                                        id="city"
                                        label="City"
                                        type="text"
                                        name="city"
                                        placeholder="Type here..."
                                        onChange={handleInput}
                                        value={memberInfo?.city}
                                        error={formErrors && formErrors.city && formErrors.city[0] !== ''}
                                        errorMessage={formErrors && formErrors.city && formErrors.city[0]}
                                    />
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography sx={styles.profileField} component="div">
                                    <InputField
                                        required
                                        id="address2"
                                        label="Apt Number"
                                        type="text"
                                        name="address2"
                                        placeholder="Type here..."
                                        onChange={handleInput}
                                        value={memberInfo?.address2}
                                        error={formErrors && formErrors.address2 && formErrors.address2[0] !== ''}
                                        errorMessage={formErrors && formErrors.address2 && formErrors.address2[0]}
                                    />
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography sx={styles.addressField2} component="div">
                                            <CustomSelect
                                                name="state"
                                                label="State"
                                                id="state"
                                                handleOnchange={handleInput}
                                                items={USState}
                                                value={memberInfo?.state}
                                                error={formErrors && formErrors.state && formErrors.state[0] !== ''}
                                                errorMessage={formErrors && formErrors.state && formErrors.state[0]}
                                                customStyles={{ height: 44 }}
                                            />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography sx={styles.addressField2} component="div">
                                            <InputField
                                                required
                                                id="zip"
                                                label="Zip Code"
                                                type="text"
                                                name="zip"
                                                placeholder="Type here..."
                                                onChange={handleInput}
                                                value={memberInfo?.zip}
                                                error={formErrors && formErrors.zip && formErrors.zip[0] !== ''}
                                                errorMessage={formErrors && formErrors.zip && formErrors.zip[0]}
                                            />
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                ) : null}
                {!memberAddressess?.length ? (
                    <IconButton
                        data-testid="addAddressAction"
                        sx={styles.addAddressContainer}
                        onClick={handleAddMemberPrimaryAddress}
                    >
                        <Typography sx={styles.addAddressText}>Add Primary Address</Typography>
                        <img alt="add" src="/images/add.svg" />
                    </IconButton>
                ) : (
                    memberAddressess?.length > 1 && (
                        <IconButton
                            data-testid="editAddressAction"
                            sx={styles.addAddressContainer}
                            onClick={handleUpdateAdditionalAddress}
                        >
                            <Typography sx={styles.addAddressText}>Additional Addresses</Typography>
                            <img alt="edit" src="/images/edit.png" />
                        </IconButton>
                    )
                )}
                <Grid container justifyContent="center">
                    <FormButton
                        variant="contained"
                        sx={styles.profileButton}
                        type="submit"
                        id="editMemberDetails"
                        data-testid="editMemberDetails"
                        submitting={submitting}
                    >
                        Save changes
                    </FormButton>
                </Grid>
            </Box>
        </FormsPopup>
    );
};
export default EditMember;
